// @givmo/board — legal holds, as a hold's report renders them. The Settings cards that listed the
// organization's retention schedules and holds left with D-CBG-53 (2026-11-01); the backend keeps both
// mechanisms, and a hold's report keeps its address.
//
// ══ TWO MECHANISMS THAT ARE NOT THE SAME KIND OF THING ═════════════════════════════════════════
//
// No screen shows a schedule any more, but the copy that remains must never blur the two:
//
//   * A RETENTION SCHEDULE IS A FLOOR THE ORGANIZATION ADOPTS. `minimum_retention_years` is the earliest
//     date the organization itself may choose to remove a record. Reaching it deletes NOTHING, and no
//     scheduler, sweep or job in this module ever will — there is no expiry anywhere in the design.
//     The program's ten-year floor is enforced in the service AND by a DB CHECK.
//   * A LEGAL HOLD IS A FREEZE THE ORGANIZATION PLACES. It overrides every deletion path structurally — a
//     database trigger refuses to delete any record an ACTIVE hold covers, including through the
//     sanctioned retention channel — and it is released only as a recorded act with its own actor,
//     instant and required reason. Never a silent expiry, and never retroactive: the coverage it had
//     while it stood is part of the record.
//
// ══ WHAT THIS MODULE DELIBERATELY DOES NOT DO ══════════════════════════════════════════════════
//
//   * NO CONCLUSION. There is no field, badge or sentence here saying an organization is keeping records
//     correctly, that a hold is sufficient, or that anything is preserved — plan §3.4 bans that class
//     of claim, and the terminology rail fails the build over the words it is usually written in.
//     What renders is the organization's own recorded figures and the backend's own sentences.
//   * NO FLOOR ARITHMETIC. The earliest deletion date for a RECORD depends on its provenance and on
//     `max(occurred_on, created_at)`, and the backend computes it — and puts it in the refusal
//     sentence when it refuses. A second implementation here would disagree with that sentence for
//     some record, some day, and the officer would have two dates and no way to choose.
//   * NO COUNT AGAINST AN EXPECTED TOTAL. `covered_record_count` on a hold report is the number THIS
//     READER may see, scoped exactly like the list beside it — a hold placed organization-wide also fences
//     executive-session records the reader was not a participant of. Comparing it against anything, or
//     reporting a gap, would leak the existence of what the record store withheld.

import type { OrgBoardLegalHoldOut, OrgBoardLegalHoldReportOut } from '@givmo/api-client';
import type { BadgeProps } from '@givmo/design-system';
import { humanize } from '../../lib/display';

export { displayDate, displayInstant } from '../../lib/display';

/** The Badge's tone union (the design-system doesn't re-export the tone type directly). */
type BadgeTone = NonNullable<BadgeProps['tone']>;

/**
 * Whether this hold is standing.
 *
 * Positive on `active`: the permissive default is the one that would render a standing hold as
 * released.
 */
export function isActiveHold(hold: OrgBoardLegalHoldOut): boolean {
  return (hold.status ?? '').toLowerCase() === 'active';
}

/** Badge tone for a hold's status. PURELY VISUAL — the text comes from {@link holdStatusLabel}. */
export function holdStatusTone(hold: OrgBoardLegalHoldOut): BadgeTone {
  return isActiveHold(hold) ? 'warning' : 'neutral';
}

/** How a hold's status reads. */
export function holdStatusLabel(hold: OrgBoardLegalHoldOut): string {
  const value = (hold.status ?? '').toLowerCase();
  if (value === 'active') return 'Standing';
  if (value === 'released') return 'Released';
  return humanize(hold.status);
}

/**
 * What a hold covers, in the organization's terms.
 *
 * An `organization` hold covers every board record INCLUDING ones created after it was placed, which is the
 * fact an officer most needs and the one a bare "all records" would leave them guessing about. A
 * `records` hold covers exactly what was named when it was placed and never grows — the coverage is
 * fixed at placement, which is what makes the report of it stable.
 */
export function holdScopeLine(hold: OrgBoardLegalHoldOut): string {
  const scope = (hold.scope ?? '').toLowerCase();
  if (scope === 'organization') {
    return 'Everything in this board’s minute book, including anything recorded from now on';
  }
  if (scope === 'records') return 'The records named when it was placed, and only those';
  return humanize(hold.scope);
}

/**
 * The report's own coverage line.
 *
 * ⚠ States what the number IS — what this reader may see — rather than presenting it as the hold's
 * absolute coverage. An organization-wide hold also fences closed-session records the reader was not a
 * participant of, and those are outside both this count and the list beside it. Saying "n records"
 * flat would be a claim the backend deliberately does not make.
 */
export function coverageLine(report: OrgBoardLegalHoldReportOut): string {
  const count = report.covered_record_count;
  return count === 1
    ? '1 record you can see is held by this'
    : `${count} records you can see are held by this`;
}
